import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Verified as VerifiedIcon,
  QrCode as QrCodeIcon,
  Visibility as ViewIcon,
  Block as RevokeIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import { certificateService, userService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";

const CertificateManagement = () => {
  const { user, isAdmin } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [openQRDialog, setOpenQRDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState(null);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    studentName: "",
    studentId: "",
    degree: "",
    graduationDate: "",
    signedBy: "",
    signedByTitle: "",
    studentAddress: "",
    selectedStudentId: "",
    documentFileId: "",
  });
  const [documentFile, setDocumentFile] = useState(null);

  useEffect(() => {
    loadCertificates();
    if (isAdmin()) {
      loadStudents();
    }
  }, []);

  const loadStudents = async () => {
    try {
      setLoadingStudents(true);
      const response = await userService.getStudents();
      setStudents(response.data.students);
    } catch (err) {
      console.error("Error al cargar estudiantes:", err);
      setError("Error al cargar lista de estudiantes");
    } finally {
      setLoadingStudents(false);
    }
  };

  const loadCertificates = async () => {
    try {
      setLoading(true);
      const response = await certificateService.getAll();
      setCertificates(response.data);
    } catch (err) {
      setError("Error al cargar certificados");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (certificate = null) => {
    if (certificate) {
      setEditingCertificate(certificate);
      setFormData({
        title: certificate.title,
        studentName: certificate.studentName,
        studentId: certificate.studentId,
        degree: certificate.degree,
        graduationDate: certificate.graduationDate
          ? new Date(certificate.graduationDate).toISOString().split("T")[0]
          : "",
        signedBy: certificate.signedBy,
        signedByTitle: certificate.signedByTitle,
        studentAddress: "",
        selectedStudentId: "",
      });
    } else {
      setEditingCertificate(null);
      setFormData({
        title: "",
        studentName: "",
        studentId: "",
        degree: "",
        graduationDate: "",
        signedBy: "",
        signedByTitle: "",
        studentAddress: "",
        selectedStudentId: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCertificate(null);
    setDocumentFile(null);
    setError("");
    setSuccess("");
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDocumentFile(file);
      setFormData((prev) => ({
        ...prev,
        documentFileId: file.name,
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "selectedStudentId" && value) {
      const selectedStudent = students.find((s) => s.id.toString() === value);
      if (selectedStudent) {
        setFormData({
          ...formData,
          selectedStudentId: value,
          studentName: selectedStudent.name,
          studentId: selectedStudent.email,
          studentAddress: selectedStudent.walletAddress || "",
        });
        return;
      }
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("studentName", formData.studentName);
      formDataToSend.append("studentId", formData.studentId);
      formDataToSend.append("degree", formData.degree);
      formDataToSend.append("graduationDate", formData.graduationDate);
      formDataToSend.append("signedBy", formData.signedBy);
      formDataToSend.append("signedByTitle", formData.signedByTitle);
      if (formData.studentAddress) {
        formDataToSend.append("studentAddress", formData.studentAddress);
      }
      if (documentFile) {
        formDataToSend.append("documentFile", documentFile);
      }

      if (editingCertificate) {
        await certificateService.update(editingCertificate.id, formData);
        setSuccess("Certificado actualizado exitosamente");
      } else {
        await certificateService.create(formDataToSend);
        setSuccess(
          "Certificado creado exitosamente y registrado en blockchain"
        );
      }

      handleCloseDialog();
      loadCertificates();
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar certificado");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este certificado?")) return;

    try {
      await certificateService.delete(id);
      setSuccess("Certificado eliminado exitosamente");
      loadCertificates();
    } catch (err) {
      setError("Error al eliminar certificado");
    }
  };

  const handleRevoke = async (id) => {
    if (
      !window.confirm(
        "¿Estás seguro de revocar este certificado? Esta acción no se puede deshacer."
      )
    )
      return;

    try {
      await certificateService.revoke(id);
      setSuccess("Certificado revocado exitosamente");
      loadCertificates();
    } catch (err) {
      setError("Error al revocar certificado");
    }
  };

  const handleVerify = async (id) => {
    try {
      const response = await certificateService.verifyOnChain(id);
      setSuccess(
        `Verificación: ${response.data.isValid ? "VÁLIDO" : "INVÁLIDO"}`
      );
    } catch (err) {
      setError("Error al verificar certificado");
    }
  };

  const handleShowQR = async (certificate) => {
    try {
      setSelectedCertificate(certificate);
      const response = await certificateService.getQRCode(certificate.id);
      const blob = new Blob([response.data], { type: "image/png" });
      const url = URL.createObjectURL(blob);
      setQrCodeUrl(url);
      setOpenQRDialog(true);
    } catch (err) {
      setError("Error al generar código QR");
    }
  };

  const handleViewCertificate = (certificate) => {
    setSelectedCertificate(certificate);
    setOpenViewDialog(true);
  };

  const handleDownloadQR = () => {
    if (qrCodeUrl && selectedCertificate) {
      const link = document.createElement("a");
      link.href = qrCodeUrl;
      link.download = `certificate-${selectedCertificate.id}-qr.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleSharePublicLink = (certificate) => {
    const publicUrl = `${window.location.origin}/api/certificates/${certificate.id}/verify/public`;
    navigator.clipboard
      .writeText(publicUrl)
      .then(() => {
        setSuccess("Enlace de verificación pública copiado al portapapeles");
      })
      .catch(() => {
        setError("Error al copiar enlace");
      });
  };

  const getStatusColor = (certificate) => {
    switch (certificate.status) {
      case "issued":
      case "active":
        return certificate.blockchainTxHash ? "success" : "warning";
      case "revoked":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (certificate) => {
    if (certificate.status === "revoked") return "REVOCADO";
    if (certificate.blockchainTxHash) return "EMITIDO";
    return "BORRADOR";
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="h4" component="h1">
                {isAdmin() ? "Gestión de Certificados" : "Mis Certificados"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {isAdmin()
                  ? "Administra todos los certificados del sistema"
                  : "Certificados emitidos para ti o que has creado"}
              </Typography>
            </Box>
            {isAdmin() && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Emitir Certificado
              </Button>
            )}
          </Box>
        </Paper>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Certificates Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Estudiante</TableCell>
                <TableCell>Grado</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Fecha Graduación</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {certificates.map((certificate) => (
                <TableRow key={certificate.id}>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {certificate.title}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      ID: {certificate.studentId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {certificate.studentName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {certificate.degree}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(certificate)}
                      color={getStatusColor(certificate)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(certificate.graduationDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleViewCertificate(certificate)}
                      title="Ver detalles"
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(certificate)}
                      title="Editar"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleShowQR(certificate)}
                      title="Ver código QR"
                      color="primary"
                    >
                      <QrCodeIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleVerify(certificate.id)}
                      title="Verificar en blockchain"
                      color="info"
                    >
                      <VerifiedIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleSharePublicLink(certificate)}
                      title="Compartir enlace público"
                      color="success"
                    >
                      <ShareIcon />
                    </IconButton>
                    {certificate.status !== "revoked" &&
                      certificate.blockchainTxHash && (
                        <IconButton
                          size="small"
                          onClick={() => handleRevoke(certificate.id)}
                          title="Revocar"
                          color="error"
                        >
                          <RevokeIcon />
                        </IconButton>
                      )}
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(certificate.id)}
                      title="Eliminar"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {certificates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No hay certificados registrados
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create/Edit Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingCertificate ? "Editar Certificado" : "Nuevo Certificado"}
          </DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                margin="normal"
                name="title"
                label="Título del Certificado"
                value={formData.title}
                onChange={handleInputChange}
                required
              />

              <TextField
                fullWidth
                margin="normal"
                type="file"
                label="Documento a Certificar"
                inputProps={{ accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png" }}
                onChange={handleDocumentChange}
                required={!editingCertificate}
                helperText="Archivo PDF, DOC, DOCX, JPG o PNG"
                InputLabelProps={{
                  shrink: true,
                }}
              />
              {documentFile && (
                <Typography
                  variant="caption"
                  color="success.main"
                  sx={{ display: "block", mt: 1 }}
                >
                  ✓ Archivo seleccionado: {documentFile.name}
                </Typography>
              )}

              {/* Selector de estudiantes (solo para admins) */}
              {isAdmin() && !editingCertificate && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Seleccionar Estudiante</InputLabel>
                  <Select
                    name="selectedStudentId"
                    value={formData.selectedStudentId}
                    onChange={handleInputChange}
                    label="Seleccionar Estudiante"
                    disabled={loadingStudents}
                  >
                    <MenuItem value="">
                      <em>
                        {loadingStudents
                          ? "Cargando estudiantes..."
                          : "Seleccionar estudiante"}
                      </em>
                    </MenuItem>
                    {students.map((student) => (
                      <MenuItem key={student.id} value={student.id}>
                        <Box>
                          <Typography variant="body1">
                            {student.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {student.email} • {student.role}
                            {student.walletAddress &&
                              ` • ${student.walletAddress.substring(0, 6)}...`}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    name="studentName"
                    label="Nombre del Estudiante"
                    value={formData.studentName}
                    onChange={handleInputChange}
                    required
                    disabled={
                      isAdmin() &&
                      !editingCertificate &&
                      !formData.selectedStudentId
                    }
                    helperText={
                      isAdmin() && !editingCertificate
                        ? "Se auto-completa al seleccionar estudiante"
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    name="studentId"
                    label="ID del Estudiante (Email)"
                    value={formData.studentId}
                    onChange={handleInputChange}
                    required
                    disabled={
                      isAdmin() &&
                      !editingCertificate &&
                      !formData.selectedStudentId
                    }
                    helperText={
                      isAdmin() && !editingCertificate
                        ? "Se auto-completa al seleccionar estudiante"
                        : ""
                    }
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                margin="normal"
                name="degree"
                label="Título/Grado"
                value={formData.degree}
                onChange={handleInputChange}
                required
              />

              <TextField
                fullWidth
                margin="normal"
                name="graduationDate"
                label="Fecha de Graduación"
                type="date"
                value={formData.graduationDate}
                onChange={handleInputChange}
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    name="signedBy"
                    label="Firmado por"
                    value={formData.signedBy}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    margin="normal"
                    name="signedByTitle"
                    label="Título de quien firma"
                    value={formData.signedByTitle}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
              </Grid>

              <TextField
                fullWidth
                margin="normal"
                name="studentAddress"
                label="Dirección de Wallet del Estudiante (Opcional)"
                placeholder="0x..."
                value={formData.studentAddress}
                onChange={handleInputChange}
                helperText="Dirección Ethereum/Polygon para el registro en blockchain"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingCertificate ? "Actualizar" : "Crear Certificado"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* QR Code Dialog */}
        <Dialog
          open={openQRDialog}
          onClose={() => setOpenQRDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Código QR del Certificado</DialogTitle>
          <DialogContent>
            {selectedCertificate && (
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedCertificate.title}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Estudiante: {selectedCertificate.studentName}
                </Typography>

                {qrCodeUrl && (
                  <Box sx={{ mt: 3, mb: 3 }}>
                    <img
                      src={qrCodeUrl}
                      alt="QR Code"
                      style={{
                        maxWidth: "300px",
                        width: "100%",
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        padding: "10px",
                      }}
                    />
                  </Box>
                )}

                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 2 }}
                >
                  Escanea este código QR para verificar el certificado
                  públicamente
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDownloadQR} startIcon={<DownloadIcon />}>
              Descargar QR
            </Button>
            <Button onClick={() => setOpenQRDialog(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>

        {/* View Certificate Dialog */}
        <Dialog
          open={openViewDialog}
          onClose={() => setOpenViewDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Detalles del Certificado</DialogTitle>
          <DialogContent>
            {selectedCertificate && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedCertificate.title}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Estudiante:</Typography>
                    <Typography variant="body2">
                      {selectedCertificate.studentName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">ID Estudiante:</Typography>
                    <Typography variant="body2">
                      {selectedCertificate.studentId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Título/Grado:</Typography>
                    <Typography variant="body2">
                      {selectedCertificate.degree}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">
                      Fecha de Graduación:
                    </Typography>
                    <Typography variant="body2">
                      {new Date(
                        selectedCertificate.graduationDate
                      ).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">
                      Fecha de Emisión:
                    </Typography>
                    <Typography variant="body2">
                      {new Date(
                        selectedCertificate.issueDate
                      ).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Firmado por:</Typography>
                    <Typography variant="body2">
                      {selectedCertificate.signedBy}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Título:</Typography>
                    <Typography variant="body2">
                      {selectedCertificate.signedByTitle}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Estado:</Typography>
                    <Chip
                      label={getStatusText(selectedCertificate)}
                      color={getStatusColor(selectedCertificate)}
                      size="small"
                    />
                  </Grid>
                </Grid>

                {selectedCertificate.documentHash && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">
                      Hash del Documento:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        wordBreak: "break-all",
                      }}
                    >
                      {selectedCertificate.documentHash}
                    </Typography>
                  </Box>
                )}

                {selectedCertificate.blockchainTxHash && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">
                      Hash de Transacción:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        wordBreak: "break-all",
                      }}
                    >
                      {selectedCertificate.blockchainTxHash}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenViewDialog(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default CertificateManagement;
